// 새 프로젝트 — 바닐라 dcf.html #page-new / dcf.js createProject·createFromExcel·
// onDcfFile·dcfTemplate 이식. '샘플 가정'·'빈 양식'(blank 분기)·'파일 업로드'
// (intake→finalize) 3가지 시작 방식 모두 활성.
import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { useDcf } from "../../stores/dcf";

interface Props {
  onCancel: () => void;
  /** 생성·열기 성공 시 워크스페이스 뷰로 전환 */
  onCreated: () => void;
}

type Method = "sample" | "blank" | "excel";

// 바닐라 #dcf-file accept 목록과 동일
const FILE_ACCEPT =
  ".xlsx,.xls,.xlsm,.xltx,.xltm,.pdf,.docx,.doc,.pptx,.ppt,.odp,.png,.jpg,.jpeg,.gif";

// intake 누락 필드 → 사용자 안내 라벨 (바닐라 openProject 의 missing 안내에 대응)
const MISSING_LABELS: Record<string, string> = {
  shares_outstanding: "발행주식수",
  fiscal_year_end_month: "결산월",
  valuation_date: "평가기준일",
  non_operating_bridge: "비영업자산·이자부부채",
  target_name: "회사명",
};

export default function DcfNewProject({ onCancel, onCreated }: Props) {
  const { t } = useT();
  const createFromSample = useDcf((s) => s.createFromSample);
  const createBlank = useDcf((s) => s.createBlank);
  const createFromExcel = useDcf((s) => s.createFromExcel);
  const busy = useDcf((s) => s.busy);
  const [name, setName] = useState("");
  const [date, setDate] = useState("2025-12-31");
  const [fye, setFye] = useState("12");
  const [method, setMethod] = useState<Method>("sample");
  const [fileNames, setFileNames] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const create = async () => {
    const projName = name.trim() || "무제 프로젝트";
    const projDate = date || "2025-12-31";
    if (method === "excel") {
      const files = Array.from(fileRef.current?.files || []);
      const res = await createFromExcel(files, projName, projDate, fye);
      if (!res) return;
      if (res.missing.length) {
        const labels = res.missing.map((k) => MISSING_LABELS[k] || k).join(", ");
        alert(
          "파일에서 추출했지만 확인이 필요한 항목이 있습니다:\n\n· " +
            labels +
            "\n\nAssumptions·Projection 탭에서 값을 보완한 뒤 재계산하세요.",
        );
      }
      onCreated();
      return;
    }
    const id =
      method === "blank"
        ? await createBlank(projName, projDate, fye)
        : await createFromSample(projName, projDate, fye);
    if (id) onCreated();
  };

  // 입력 엑셀 템플릿 다운로드 — blank=true 면 값이 비고 입력할 칸이 베이지로 표시된 양식
  const downloadTemplate = async (blank = false) => {
    try {
      const d = await api<{ ok: boolean; message?: string; url?: string }>(
        "/agent/dcf/template" + (blank ? "?blank=1" : ""),
      );
      if (d.ok && d.url) window.open(d.url, "_blank");
      else alert("템플릿 URL을 받지 못했습니다.");
    } catch (e) {
      alert("템플릿 다운로드 실패: " + (e instanceof Error ? e.message : String(e)));
    }
  };

  return (
    <div className="new-val">
      <h2>{t("new.title")}</h2>
      <div className="sub">{t("new.sub")}</div>
      <div className="form-card">
        <h3>{t("new.basic")}</h3>
        <div className="form-group">
          <label htmlFor="new-company">{t("lbl.company")}</label>
          <input
            type="text"
            id="new-company"
            placeholder={t("new.company_ph")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="new-date">{t("lbl.val_date")}</label>
            <input
              type="date"
              id="new-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="new-fye">{t("lbl.fye")}</label>
            <select id="new-fye" value={fye} onChange={(e) => setFye(e.target.value)}>
              <option value="12">{t("opt.m12")}</option>
              <option value="3">{t("opt.m3")}</option>
              <option value="6">{t("opt.m6")}</option>
              <option value="9">{t("opt.m9")}</option>
            </select>
          </div>
        </div>
      </div>
      <div className="form-card">
        <h3>{t("new.start_data")}</h3>
        <div className="method-grid">
          <div
            className={`method-card${method === "sample" ? " selected" : ""}`}
            onClick={() => setMethod("sample")}
          >
            <h4>{t("new.sample")}</h4>
            <p>{t("new.sample_desc")}</p>
          </div>
          <div
            className={`method-card${method === "blank" ? " selected" : ""}`}
            onClick={() => setMethod("blank")}
          >
            <h4>{t("new.blank")}</h4>
            <p>{t("new.blank_desc")}</p>
          </div>
          <div
            className={`method-card${method === "excel" ? " selected" : ""}`}
            onClick={() => setMethod("excel")}
          >
            <h4>{t("new.upload")}</h4>
            <p>{t("new.upload_desc")}</p>
          </div>
        </div>
        {method === "excel" && (
          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: "var(--border)",
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept={FILE_ACCEPT}
              multiple
              style={{ display: "none" }}
              onChange={(e) =>
                setFileNames(Array.from(e.target.files || []).map((f) => f.name))
              }
            />
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button
                className="report-btn primary"
                type="button"
                onClick={() => fileRef.current?.click()}
              >
                {t("new.file_select")}
              </button>
              <a
                className="dl-link"
                href="#template"
                style={{ marginTop: 0 }}
                onClick={(e) => {
                  e.preventDefault();
                  void downloadTemplate(false);
                }}
              >
                {t("new.template")}
              </a>
              <a
                className="dl-link"
                href="#template-blank"
                style={{ marginTop: 0 }}
                onClick={(e) => {
                  e.preventDefault();
                  void downloadTemplate(true);
                }}
              >
                {t("new.template_blank")}
              </a>
            </div>
            <div className="hint" style={{ marginTop: 10 }}>
              {fileNames.length
                ? t("new.file_selected") + fileNames.join(", ")
                : t("new.file_hint")}
            </div>
          </div>
        )}
      </div>
      <div className="form-actions">
        <button className="btn-cancel" onClick={onCancel}>
          {t("new.cancel")}
        </button>
        <button className="btn-start" disabled={!!busy} onClick={() => void create()}>
          {t("new.create")}
        </button>
      </div>
    </div>
  );
}
